import type { ColDef, GetQuickFilterTextParams } from "ag-grid-enterprise";
import { mapColDefTree } from "../col_def_tree";

// AG Grid's quick filter concatenates EVERY column's text, so typing "running" or an
// organization name matched rows the user saw no reason for. Scoping it is per-column:
// one column carries the whole searchable string — built from row-data fields, so a field
// with no column of its own (`id`) is still searchable — and every other column
// contributes the empty string.
//
// The carrier must be a visible column: quick filter skips hidden ones unless
// includeHiddenColumnsInQuickFilter is set, and these grids leave it off.
//
// Inert on the vote-by-vote scored grid today — that host passes no quick-filter fields.
export function withQuickFilterFields(columnDefs: ColDef[], fields: string[]): ColDef[] {
  if (!fields.length || !columnDefs.length) return columnDefs;

  const isLeaf = (col: ColDef) => !Array.isArray((col as { children?: unknown }).children);

  // The carrier is elected from TOP-LEVEL LEAF defs only — AG Grid never calls
  // getQuickFilterText on a ColGroupDef, so electing a group would silently match nothing.
  // Exactly one carrier grid-wide, never one per group.
  const leafIndices = columnDefs.reduce<number[]>(
    (acc, col, index) => (isLeaf(col) ? [...acc, index] : acc),
    []
  );
  const visibleLeafIndex = leafIndices.find((index) => columnDefs[index].hide !== true);
  const carrierIndex = visibleLeafIndex ?? leafIndices[0] ?? -1;

  const carrierGetter = (params: GetQuickFilterTextParams) =>
    fields
      .map((field) => (params.data as Record<string, unknown> | undefined)?.[field] ?? "")
      .join(" ");
  const emptyGetter = () => "";

  return columnDefs.map((col, index) => {
    if (!isLeaf(col)) {
      // Every nested leaf under a group contributes the empty string — never a carrier.
      return {
        ...col,
        children: mapColDefTree((col as { children: ColDef[] }).children, (leaf) => ({
          ...leaf,
          getQuickFilterText: emptyGetter,
        })),
      };
    }
    return { ...col, getQuickFilterText: index === carrierIndex ? carrierGetter : emptyGetter };
  });
}
